import React, { useCallback } from 'react';
import Tooltip from 'components/common/tooltip';
import isEmpty from 'lodash/isEmpty';
import styled from 'types/theme.types';
import { useNavigate } from 'react-router';
import { getProjectHierarchyDisplaySegements } from 'utils/get-project-hierarchy-display-segements';
import { typeStyle } from 'constants/typography';

interface RecentProjectSegment {
  id: string;
  name: string;
}

interface RecentProject {
  id: string;
  name: string;
  segments: RecentProjectSegment[];
}

interface TopbarProjectSelectorRecentProjectsProps {
  recentItems: RecentProject[];
  currentProjectId?: string;
  onClose: () => void;
  maxWidthPx: number;
}

function renderPath(segments: RecentProjectSegment[], fullPathText: string) {
  const items: React.ReactNode[] = [];

  segments.forEach((seg, index) => {
    const isLastPathSegment = index === segments.length - 1;
    if (seg.id === 'ELLIPSIS') {
      items.push(
        <Tooltip
          key={`ellipsis-${index}`}
          title={fullPathText}
          placement="top"
          mouseEnterDelay={0.3}
          trigger={['hover']}>
          <Ellipsis aria-label={fullPathText}>...</Ellipsis>
        </Tooltip>
      );
    } else {
      items.push(
        <PathSegment $isLast={isLastPathSegment} aria-label={seg.name}>
          {seg.name}
        </PathSegment>
      );
    }

    if (!isLastPathSegment) {
      items.push(<Separator key={`sep-${index}`}>{'>'}</Separator>);
    }
  });

  return items;
}

const TopbarProjectSelectorRecentProjects: React.FC<TopbarProjectSelectorRecentProjectsProps> = ({
  recentItems,
  currentProjectId,
  onClose,
  maxWidthPx
}) => {
  const HORIZONTAL_PADDING_PX = 24;
  const navigate = useNavigate();
  const navigateAndClose = useCallback(
    (projectId: string) => {
      if (projectId !== currentProjectId) {
        navigate(`/p/${projectId}/environments`);
      }
      onClose();
    },
    [currentProjectId, navigate, onClose]
  );

  if (isEmpty(recentItems)) return null;

  return (
    <RecentContainer>
      <RecentTitle>Recent</RecentTitle>
      {recentItems.map(item => {
        const fullPath = item.segments.map(s => s.name).join(' > ');
        const displaySegments = getProjectHierarchyDisplaySegements(item.segments, {
          maxWidthPx: maxWidthPx - HORIZONTAL_PADDING_PX,
          // Based on recent item styling
          pxPerChar: 7
        });

        return (
          <RecentItem
            key={item.id}
            data-e2e={`recent-project-${item.id}`}
            role="button"
            onClick={() => navigateAndClose(item.id)}>
            <RecentPath>{renderPath(displaySegments, fullPath)}</RecentPath>
          </RecentItem>
        );
      })}
    </RecentContainer>
  );
};

const RecentContainer = styled.div`
  padding: 0;
  border-bottom: 1px solid var(--border-primary);
  background: var(--surface-main-cards);
`;

const RecentTitle = styled.div`
  ${typeStyle.labelSm}
  color: var(--text-heading);
  opacity: 0.8;
`;

const RecentItem = styled.div`
  padding: 5px 8px;
  margin-bottom: 4px;
  border-radius: 4px;
  transition:
    background-color 0.2s ease,
    color 0.2s ease;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: var(--surface-main-element-on-card);
    color: var(--text-body);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const RecentPath = styled.div`
  font-size: 13px;
  line-height: 16px;
  color: var(--text-body);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
  overflow: hidden;
  min-width: 0;
`;

const PathSegment = styled.span<{ $isLast?: boolean }>`
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: inherit;
  color: var(--text-body);
  opacity: ${({ $isLast }) => ($isLast ? 1 : 0.5)};
  white-space: nowrap;
  display: block; /* allow text-overflow to take effect */
  min-width: 0;
  flex: ${({ $isLast }) => ($isLast ? '1 1 auto' : '0 0 auto')};
  max-width: ${({ $isLast }) => ($isLast ? '100%' : 'none')};
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    text-decoration: none;
  }
`;

const Separator = styled.span`
  color: var(--text-body);
  opacity: 0.5;
  cursor: inherit;
`;

const Ellipsis = styled.span`
  color: var(--text-body);
  opacity: 0.5;
  cursor: inherit;
  white-space: nowrap;
  display: inline-block;
  padding: 2px 6px;
  margin: 0 -6px;
  border-radius: 4px;
`;

export default TopbarProjectSelectorRecentProjects;
